import { Box, Button } from '@mui/material';
import { FormikProps } from 'formik';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { NContact } from '@modules/ContactManager/components/Tabs/components/Contact/validation';
import { DealProduct } from '@modules/ContactManager/components/Tabs/components/Deals/components/Products/interfaces';
import { formatDate } from '@shared/commonFunctions';
import { useAppDispatch } from '@store/hooks';

import { footerLenderSelector, setFooterLenderAction } from '@store/reducers/dealCalculatorSlice';
import {
  LenderbuttonWrapper, LenderSectionDivider,
  LenderSectionWrapper
} from '@styles/ContactManager/DealCalculator/LenderSectionStyles';
import { colorTheme, extraColor } from '@styles/muiTheme';

import { DealCalculatorModel } from '../../../../models/DealCalculatorModel';
import { IEligibleLender } from '../../../../models/EligibleLenderModel';
import { ILenderFooterState } from '../../../../interfaces';
import LenderCreditRules from './LenderCreditRules';
import LenderInputs from './LenderInputs';
import LenderPitch from './LenderPitch';
import LenderResult from './LenderResult';
import LenderSectionProducts, { ILenderResponseFooter } from './LenderSectionProducts';
import {
  addDays,
  dailyCalculations,
  maxLtvGreen, PMT, tierConditionByMaxLtvGreen,
  tierConditionByTerm
} from './lernderCalculationUtils';
import { IAddRequestDeal, requestActualDTI, requestUpsertDeal } from './queries';


interface ILenderSectionProps {
  idx: number;
  lender: IEligibleLender;
  isSelected: boolean;
  coContactId?: string;
  isExpanded(section: string): boolean;
  setSelected(): void;
  formProps: FormikProps<DealCalculatorModel>;
  leadId?: string;
  assetId: string;
  dealId?: string;
  contactAddress?: NContact.Address;
  isVoidEnable: boolean;
  borrowerName?: string;
  coBorrowerName?: string;
  setNewDealId: React.Dispatch<React.SetStateAction<string | null>>;
  setBtnLoading: React.Dispatch<React.SetStateAction<boolean>>;
  reloadDealCalculator?: () => void;
  reloadContactAlerts: () => void;
}

const lenderCodes = {
  wr: 'wr',
  fcu: 'fcu',
  frs: 'frs',
  gldo: 'gldo',
  mat: 'mat',
  ki: 'ki',
  eg: 'eg',
  hughes: 'hughes',
};

const getDownPaymentColorCoding = (value: number, requiredDownPayment: number) => {
  let color = 'transparent';

  if (value > requiredDownPayment) {
    color = colorTheme.palette.orange[200] as string;
  } else if (value < requiredDownPayment) {
    color = extraColor.palette.error.secondary;
  }

  return color;
};

export const LenderSection = ({
  idx,
  lender,
  isSelected,
  coContactId,
  setSelected,
  isExpanded,
  formProps,
  leadId,
  assetId,
  dealId,
  contactAddress,
  setNewDealId,
  setBtnLoading,
  isVoidEnable,
  reloadDealCalculator,
  reloadContactAlerts,
}: ILenderSectionProps) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const initCashOut: any = useRef();
  const [lenderResultFooterData, setLenderResultFooterData] = useState<ILenderResponseFooter>({
    gpVrp: 0,
    totalGp: 0,
  });

  const [dealProducts, setDealProducts] = useState<DealProduct[]>([]);
 const footerLender: Partial<ILenderFooterState> | null = useSelector(footerLenderSelector);
  const { values } = formProps;
  const {
    downPayment = 0,
    cashOut: cashOutGlobal = 0,
    term: termGlobal = 0,
    rateMarkup: rateMarkupGlobal = 0,
    salesPrice: salesPriceGlobal = 0,
    lienRate: lienRate = 0,
    lienPayment: lienPayment = 0,
    dealCurrentWorkflowStatus: dealStatus = '',
    gapRefund: gapRefund = 0,
    vscRefund: vscRefund = 0,
    salesTaxPercentage = 0,
  } = values.globalInputs;
  const {
    rateMarkup: rateMarkupLocal = 0,
    term: termLocal = 0,
    downPayment: downPaymentLocal = 0,
    lenderCode,
    cashOut: cashOutLocal = 0,
    approvedRate = 0,
    contractDate = '',
    firstPaymentDays = 0,
  } = lender.inputs;
  const { lenderCreditRules: creditRules = [] } = lender;
  const firstPaymentDate = addDays(contractDate, firstPaymentDays);
  const lenderCodeLocal = lenderCode?.toLowerCase();
  const docFee = lender.totalDealFeeAmount;
  const valuation = lender?.valuation || 0;
  const productSalesTax = 0; // TODO productSalesTax - not found - taxAmount from products-service -> leads/dealCalculato

  const checkLenderCode = (code: string) => {
    return lender.code === code;
  };

  const { pafRetail = 0, vscRetail = 0, gapRetail = 0, comRetail = 0, vrpRetail = 0 } = lender.inputs;
  const subTotalBeforeTax = (): number => {
    if (checkLenderCode(lenderCodes.wr)) {
      return salesPriceGlobal + pafRetail + vscRetail + gapRetail + comRetail + vrpRetail;
    }
    return salesPriceGlobal + docFee + pafRetail + vscRetail + gapRetail + comRetail + vrpRetail;
  };

  const salesTax = subTotalBeforeTax() * (salesTaxPercentage / 100);
  const frontEndNeed = () => {
    if (checkLenderCode(lenderCodes.wr)) {
      return salesPriceGlobal + salesTax + pafRetail + cashOutLocal + vrpRetail;
    }
    return salesPriceGlobal + salesTax + docFee + pafRetail + cashOutLocal + vrpRetail;
  };
  const frontEnd = (): number => {
    return frontEndNeed() - downPaymentLocal;
  };
  const frontEndLtv = () => {
    if (valuation === 0) {
      return 0;
    }
    return (frontEnd() / valuation) * 100;
  };

  const totalAmountFinanced = frontEnd() + vscRetail + gapRetail + comRetail + productSalesTax;
  const allInLtv = () => {
    if (valuation === 0) {
      return 0;
    }
    return (totalAmountFinanced / valuation) * 100;
  };
  const LTV = () => {
    if (valuation === 0) {
      return 0;
    }
    if (lender?.ltvType === 'FrontEnd') {
      return frontEndLtv();
    }
    return allInLtv();
  };

  const selectedTierCondition = () => {
    if (checkLenderCode(lenderCodes.fcu) || checkLenderCode(lenderCodes.frs) || checkLenderCode(lenderCodes.gldo)) {
      return tierConditionByMaxLtvGreen(lender, LTV());
    }
    return tierConditionByTerm(lender, LTV(), cashOutLocal, termLocal);
  };

  const maxLtvYellow = selectedTierCondition()?.maxLtvYellow || 0;
  // const tierLabel = selectedTierCondition()?.tierLabel || 0;
  const maxLoanAmount = selectedTierCondition()?.maxLoanAmount || 0;
  const rateAdjustment = selectedTierCondition()?.rateAdjustment || 0; // TODO: rateAdjustment not found -  from selectedTierCondition(formula)
  const buyRate = selectedTierCondition()?.rate || 0;
  const netRate = buyRate + rateMarkupLocal + rateAdjustment;

  const lenderRateOptions = useMemo(() => {
    return lender.availableMarkupIncrements
    ? lender.availableMarkupIncrements.split(',').map((rate: string) => ({ value: +rate, label: rate + '%' }))
    : [];
  }, []);

  const tierConditionsFirstTermGroup = useMemo(() =>
    Math.max(...lender.tierConditions.map(tierCondition => tierCondition.termGroup ?? 0)),
    [lender.tierConditions]);

  useEffect(() => {
    initCashOut.current = {
      cashOut: cashOutGlobal,
      downPayment,
      termGlobal,
      rateMarkupGlobal,
      termLocal
    };
  }, []);


  useEffect(() => {
    formProps.setFieldValue(`eligibleLenders[${idx}].inputs.approvedRate`, buyRate);
    if (initCashOut.current.cashOut !== cashOutGlobal) {
      formProps.setFieldValue(`eligibleLenders[${idx}].inputs.cashOut`, cashOutGlobal);
      initCashOut.current.cashOut = cashOutGlobal;
    }
    if (initCashOut.current.downPayment !== downPayment) {
      formProps.setFieldValue(`eligibleLenders[${idx}].inputs.downPayment`, downPayment);
      initCashOut.current.downPayment = downPayment;
    }
    if (initCashOut.current.termGlobal !== termGlobal) {
      formProps.setFieldValue(`eligibleLenders[${idx}].inputs.term`, (termGlobal > tierConditionsFirstTermGroup) ? tierConditionsFirstTermGroup : termGlobal);
      formProps.setFieldValue(`eligibleLenders[${idx}].inputs.approvedRate`, netRate);
      initCashOut.current.termGlobal = termGlobal;
    }
    if (initCashOut.current.rateMarkupGlobal !== rateMarkupGlobal) {
      // set lender rate only if that rate option is available for lender
      if (lenderRateOptions.find(lr => lr.value == rateMarkupGlobal)) {
        formProps.setFieldValue(`eligibleLenders[${idx}].inputs.rateMarkup`, rateMarkupGlobal);
        initCashOut.current.rateMarkupGlobal = rateMarkupGlobal;
      }
    }
    // if apporved rate is coign zero from APi set default to netrate
    if (approvedRate <= 0 || initCashOut.current.termLocal !== termLocal) {
      formProps.setFieldValue(`eligibleLenders[${idx}].inputs.approvedRate`, netRate);
      initCashOut.current.termLocal = termLocal;
    }

  }, [cashOutGlobal, downPayment, termGlobal, rateMarkupGlobal, lenderRateOptions, tierConditionsFirstTermGroup, termLocal, approvedRate, netRate]);

  const getColorCoding = (value = 0, valueYellow: number, valueGreen: number) => {
    if (valueYellow === 0 && valueGreen > 0) {
      valueYellow = valueGreen;
    }

    if (valueGreen < value && value <= valueYellow) {
      return extraColor.palette.orange[400]; // yellow
    }

    if (value > valueYellow) {
      return extraColor.palette.error.secondary; // red
    }

    return 'transparent';
  };

  const maxAllInLtv = selectedTierCondition()?.maxAllInLtv || 0;

  const isTotalAmountFinancedRed = () => {
    const isRed =
      totalAmountFinanced > maxLoanAmount ||
      ((checkLenderCode(lenderCodes.eg) || checkLenderCode(lenderCodes.fcu)) && allInLtv() > maxAllInLtv);
    const otherColor = getColorCoding(
      totalAmountFinanced,
      selectedTierCondition()?.minTotalAmountFinanceGreen || 0,
      selectedTierCondition()?.minTotalAmountFinanceYellow || 0
    );
    return isRed ? '#f44336cf' : otherColor;
  };

  const addDealHandler = useCallback(async () => {
    setBtnLoading(true);
    const isDraftDeal = false;
    const postData: IAddRequestDeal = {
      lenderCode: lenderCodeLocal,
      rate: approvedRate || netRate, // approved should be default to netRate if 0
      rateMarkUp: rateMarkupLocal,
      netRate,
      term: termLocal,
      loanAmount: salesPriceGlobal,
      totalAmountFinanced,
      type: 1,
      dealUsers: null,
      isPreApproval: false,
      assetId: assetId,
      products: dealProducts,
      cashout: cashOutLocal,
      downPayment: downPaymentLocal,
      salesTaxPercentage
    };

    const response = await requestUpsertDeal(leadId || '', dealId || '', postData, isDraftDeal);
    !isDraftDeal && setNewDealId(response?.id ?? null);
    setBtnLoading(false);
  }, [
    lenderCodeLocal,
    approvedRate,
    termLocal,
    totalAmountFinanced,
    assetId,
    dealProducts,
    leadId,
    dealId,
    dealStatus,
    netRate,
    rateMarkupLocal,
    salesPriceGlobal,
    cashOutLocal,
    downPaymentLocal,
    salesTaxPercentage
  ]);

  const dispatch = useAppDispatch();
  useEffect(() => {
    isSelected &&
        dispatch(
          setFooterLenderAction({
            rate: lender.inputs?.approvedRate,
            term: termLocal,
            remainingTerm: termLocal,
            GAP: gapRetail,
            VSC: vscRetail,
            PAF: pafRetail,
            downPayment: lender.inputs?.downPayment,
            paymentDate: formatDate(firstPaymentDate.toISOString()),
          })
        );
  }, [lender.inputs?.approvedRate, termLocal, gapRetail, vscRetail, pafRetail, downPayment, idx, lender, isSelected]);

  useEffect(() => {
    if (!isSelected || !footerLender?.payment) return;
    const fetchDTI = async () => {
      const newDTI = await requestActualDTI(
        leadId || '',
        footerLender.payment ?? 0,
        lender?.primaryBorrowerBureau?.preferredBureau || '',
        lender.primaryBorrowerBureau.defaultBureau || ''
      );

      dispatch(setFooterLenderAction({ currentDTI: lender?.actualDti, newDTI }));
    };

    fetchDTI();
  }, [
    leadId,
    footerLender?.payment,
    lender?.primaryBorrowerBureau?.preferredBureau,
    lender.primaryBorrowerBureau.defaultBureau,
    lender?.actualDti,
    isSelected,
  ]);



  const onPickButtonClick = useCallback(() => {
    let payment = 0;
    const localTerm = lender.inputs?.term || 0;
    if (checkLenderCode('mat')) {
      const calculationResult = dailyCalculations(
        totalAmountFinanced,
        netRate / 100,
        localTerm,
        contractDate,
        firstPaymentDate.toString()
      );
      payment = calculationResult?.firstPaymentAmount || 0;
    } else {
      const amount = Number((-1 * PMT(netRate / 1200, localTerm, totalAmountFinanced, firstPaymentDays, 0, 0)).toFixed());
      payment = amount;
    }

    dispatch(
      setFooterLenderAction({
        rate: lender.inputs?.approvedRate,
        term: termLocal,
        remainingTerm: termLocal,
        GAP: gapRetail,
        VSC: vscRetail,
        PAF: pafRetail,
        downPayment: lender.inputs?.downPayment,
        payment,
        paymentDate: formatDate(firstPaymentDate.toISOString()),
      })
    );
  }, [
    lender,
    lender.inputs?.approvedRate,
    termLocal,
    gapRetail,
    vscRetail,
    pafRetail,
    idx,
    totalAmountFinanced,
    netRate,
    contractDate,
    firstPaymentDays,
  ]);

  const maxTermRed = useCallback(() => {
    let termBgColor = '';

    if (termLocal > tierConditionsFirstTermGroup || termGlobal > tierConditionsFirstTermGroup) {
      termBgColor = extraColor.palette.error.secondary;
    }

    return termBgColor;
  }, [tierConditionsFirstTermGroup, termLocal]);

  return (
    <>
      <LenderSectionWrapper
        selected={isSelected}
        onClick={setSelected}
      >
        <LenderInputs
          {...{
            lender,
            idx,
            valuation,
            LTV,
            maxLtvGreen: maxLtvGreen(lender, termLocal),
            maxLtvYellow,
            getColorCoding,
            getDownPaymentColorCoding,
            isExpanded,
            coContactId,
            tierConditionsFirstTermGroup,
            maxTermRed,
            reloadDealCalculator,
            reloadContactAlerts
          }}
        />
        <LenderSectionDivider />
        <LenderSectionProducts
          isExpanded={isExpanded}
          leadId={leadId}
          contactAddress={contactAddress}
          lender={lender}
          lenderCode={lenderCodeLocal}
          idx={idx}
          maxProduct={lender.maxProduct}
          formProps={formProps}
          setLenderResultFooterData={setLenderResultFooterData}
          getProducts={setDealProducts}
          term={lender.inputs.term || 0}
          isVoidEnable={isVoidEnable}
        />
        <LenderSectionDivider />
        <Box height={29} />
        <LenderResult
          {...{
            lender,
            idx,
            formProps,
            getColorCoding,
            netRate,
            subTotalBeforeTax,
            salesTax,
            productSalesTax,
            isTotalAmountFinancedRed,
            totalAmountFinanced,
            checkLenderCode,
            lenderResultFooterData,
            rateSaving: lienRate - netRate,
            lienPayment,
            buyRate,
            rateAdjustment,
            lenderRateOptions,
            isExpanded,
            isSelected,
          }}
        />
        <LenderSectionDivider style={{ marginBottom: '48px' }} />
        <LenderCreditRules lenderCreditRules={creditRules} />
        <LenderSectionDivider style={{ marginBottom: '48px' }} />
        {isExpanded('pitch') && (
          <LenderPitch
            {...{
              lender,
              idx,
              formProps,
              totalAmountFinanced,
              netRate,
              lienPayment,
              dealStatus,
              estimatedRefunds: gapRefund + vscRefund,
            }}
          />
        )}
        <LenderSectionDivider style={{ marginBottom: '48px', paddingBottom: `${isExpanded('pitch') ? 0 : '15px'}` }} />
        <LenderbuttonWrapper>
          <Button
            variant="contained"
            style={{ marginBottom: '10px' }}
            onClick={onPickButtonClick}
          >
            Pick
          </Button>
          <Button
            variant="contained"
            style={{ marginBottom: '10px' }}
            onClick={addDealHandler}
          >
            + App
          </Button>
        </LenderbuttonWrapper>
      </LenderSectionWrapper>
    </>
  );
};
